import html2canvas from "html2canvas-pro";

/**
 * Generates stamped background images for a stamp pass
 * @param {HTMLElement} previewElement - The preview element to generate stamps from
 * @param {number} stampCount - Total number of stamps
 * @param {Object} options - Additional options for stamp generation
 * @param {string} options.stampIconColor - Color of the stamp icon
 * @param {number} options.backgroundColor - Background color of the stamp icon
 * @returns {Promise<Blob[]>} Array of image blobs for each stamp variation
 */
export const generateStampedImages = async (
  previewElement,
  stampCount,
  options = {},
) => {
  const { backgroundColor = null } = options;

  try {
    if (!previewElement) {
      throw new Error("Preview element not found");
    }

    const variations = [];
    const clonedElement = previewElement.cloneNode(true);

    // Remove size constraints from the cloned element
    clonedElement.style.minWidth = "auto";
    clonedElement.style.maxWidth = "none";
    clonedElement.style.width = "1140px";
    clonedElement.style.height = "435px";

    // Create a temporary container
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.left = "-9999px";
    container.style.width = "1140px";
    container.style.height = "435px";
    container.appendChild(clonedElement);
    document.body.appendChild(container);

    // Scale up stamp elements
    const stampElements = clonedElement.querySelectorAll(".stamp-icon");
    const stampContainers = clonedElement.querySelectorAll(".stamp-container");

    stampContainers.forEach((container) => {
      const currentSize = parseInt(container.style.width);
      const newSize = currentSize * 3; // Triple the size
      container.style.width = `${newSize}px`;
      container.style.height = `${newSize}px`;
    });

    stampElements.forEach((stamp) => {
      const currentSize = parseInt(stamp.style.width);
      const newSize = currentSize * 3; // Triple the size
      stamp.style.width = `${newSize}px`;
      stamp.style.height = `${newSize}px`;
    });

    // Generate each variation
    for (let filledCount = 0; filledCount <= stampCount; filledCount++) {
      // Update stamp opacities
      stampElements.forEach((stamp, index) => {
        stamp.style.opacity = index < filledCount ? "1" : "0";
      });

      // Wait for a frame to ensure DOM updates
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // Capture the current state with correct dimensions
      const canvas = await html2canvas(clonedElement, {
        width: 1140,
        height: 435,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: backgroundColor,
        logging: false,
      });

      // Convert to blob
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.9),
      );
      variations.push(blob);
    }

    // Clean up
    document.body.removeChild(container);
    return variations;
  } catch (error) {
    console.error("Error generating stamped images:", error);
    throw error;
  }
};

/**
 * Creates object URLs for an array of blobs
 * @param {Blob[]} blobs - Array of image blobs
 * @returns {string[]} Array of object URLs
 */
export const createPreviewUrls = (blobs) => {
  return blobs.map((blob) => URL.createObjectURL(blob));
};

/**
 * Appends stamped images to FormData
 * @param {FormData} formData - FormData instance to append to
 * @param {Blob[]} stampedBlobs - Array of stamped image blobs
 * @param {number} startIndex - Starting index for image naming
 */
export const appendStampedImages = (formData, stampedBlobs, startIndex = 0) => {
  stampedBlobs.forEach((blob, i) => {
    const index = startIndex + i;
    formData.append(
      `backgroundImage_${index}`,
      blob,
      `background-${index}.png`,
    );
  });
};

/**
 * Generates a preview image from a pass preview container element
 * @param {HTMLElement} previewElement - The preview element to generate image from
 * @returns {Promise<string>} Base64 encoded image data
 */
export const generatePreviewImage = async (previewElement) => {
  console.log("generatePreviewImage", previewElement);
  if (!previewElement) {
    throw new Error("Preview element not found");
  }

  console.log("previewElement", previewElement);

  const canvas = await html2canvas(previewElement, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    imageTimeout: 0,
    logging: true,
    backgroundColor: null,
  });

  return canvas.toDataURL("image/png", 0.9);
};
